import { useEffect, useRef, useState } from "react";
import i18n from "../../language/i18n";
import { formatRemainingDuration } from "../../utilization/DurationUtilization";

type CountdownUnit = "day" | "hour" | "minute" | "second";

interface LiveCountdownProps {
  // Epoch ms the countdown targets (e.g. an assignment's beginAt).
  targetAt: number;
  // Fired exactly once when the countdown reaches zero — callers use it to re-render
  // and let the assignment "open" (show the Start button) without a manual refresh.
  onComplete?: () => void;
  // "compact" (default) = a calm one-line "Başlamasına 5 saat 43 dakika" on a minute
  // cadence, for list cards + the hero chip. "tiles" = segmented number tiles
  // (05 SAAT · 43 DAKİKA · 11 SANİYE) ticking every second in the final day, for the
  // prominent detail banner.
  variant?: "compact" | "tiles";
  // compact only: wrap the value via i18n interpolation, e.g. "begins_in" →
  // "Başlamasına {{duration}}". Ignored for the tiles variant.
  prefixKey?: string;
  className?: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;
const pad = (n: number) => String(n).padStart(2, "0");

// Tile labels (full words; uppercased by CSS). Dedicated keys because duration_* is
// abbreviated in EN ("h"/"m") which is too terse for a labelled tile.
const UNIT_LABEL_KEY: Record<CountdownUnit, string> = {
  day: "countdown_unit_days",
  hour: "countdown_unit_hours",
  minute: "countdown_unit_minutes",
  second: "countdown_unit_seconds",
};

// The three most-significant units to show: days/hours/minutes once ≥ 1 day (seconds
// would be noise at that range), else hours/minutes/seconds for the live final-day tick.
const retrieveSegments = (
  remaining: number,
): { value: number; unit: CountdownUnit }[] => {
  const totalSeconds = Math.floor(remaining / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (days >= 1) {
    return [
      { value: days, unit: "day" },
      { value: hours, unit: "hour" },
      { value: minutes, unit: "minute" },
    ];
  }
  return [
    { value: hours, unit: "hour" },
    { value: minutes, unit: "minute" },
    { value: seconds, unit: "second" },
  ];
};

// A single ticking countdown. Self-schedules its next tick at the right cadence and
// stops (firing onComplete) once the target time is reached.
const LiveCountdown = ({
  targetAt,
  onComplete,
  variant = "compact",
  prefixKey,
  className,
}: LiveCountdownProps) => {
  const [now, setNow] = useState(() => Date.now());
  const firedRef = useRef(false);
  const isTiles = variant === "tiles";

  useEffect(() => {
    const remaining = targetAt - now;
    if (remaining <= 0) return; // reached zero — no further ticks scheduled
    // Tiles show seconds in the final day, so they need a second cadence there; the
    // compact coarse text only shows minutes, so a calm minute cadence suffices.
    const base = isTiles && remaining < DAY_MS ? 1000 : 60000;
    // Clamp the last tick to land exactly at zero: when less than one cadence
    // remains (e.g. a compact minute-cadence countdown in its final 60s), fire at
    // `remaining` rather than a full cadence later — otherwise onComplete (the row
    // "unlock" flipping Yakında → Başla) could trigger up to ~60s after beginAt.
    const delay = Math.min(base, remaining);
    const id = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(id);
  }, [now, targetAt, isTiles]);

  useEffect(() => {
    if (targetAt - now <= 0 && !firedRef.current) {
      firedRef.current = true;
      onComplete && onComplete();
    }
  }, [now, targetAt, onComplete]);

  const remaining = Math.max(0, targetAt - now);

  if (isTiles) {
    return (
      <span className={`lcd-tiles${className ? ` ${className}` : ""}`}>
        {retrieveSegments(remaining).map((segment) => (
          <span className="lcd-tile" key={segment.unit}>
            <span className="lcd-tile-num">{pad(segment.value)}</span>
            <span className="lcd-tile-lbl">
              {i18n.t(UNIT_LABEL_KEY[segment.unit])}
            </span>
          </span>
        ))}
      </span>
    );
  }

  const value = formatRemainingDuration(remaining);
  const text = prefixKey ? i18n.t(prefixKey, { duration: value }) : value;
  return (
    <span className={className} style={{ fontVariantNumeric: "tabular-nums" }}>
      {text}
    </span>
  );
};

export default LiveCountdown;
